import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import './AuthForm.css';
import Footer from '../Footer/Footer';
import Navbar from '../Navbar/Navbar';

// --- SVG Icons ---
const PhoneIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>;
const KeyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.629 5.875a6 6 0 01-1.32 1.405l-4.148 4.148a2 2 0 01-2.828 0l-2.828-2.828a2 2 0 010-2.828l4.148-4.148A6 6 0 0115 7z" /></svg>;

const RESEND_COOLDOWN_SECONDS = 30;

const AuthForm = () => {
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // --- RESEND OTP STATE ---
    const [resendCooldown, setResendCooldown] = useState(0);
    const [timerId, setTimerId] = useState(null);

    const { login, requestPatientOtp } = useAuth();
    const navigate = useNavigate();

    // --- EFFECT FOR TIMER CLEANUP ---
    useEffect(() => {
        // This function will be called when the component unmounts
        return () => {
            if (timerId) {
                clearInterval(timerId);
            }
        };
    }, [timerId]);

    // --- FUNCTION TO START THE RESEND COOLDOWN TIMER ---
    const startResendTimer = () => {
        if (timerId) {
            clearInterval(timerId);
        }
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        const newTimerId = setInterval(() => {
            setResendCooldown(prevCooldown => {
                if (prevCooldown <= 1) {
                    clearInterval(newTimerId);
                    return 0; // Stop the countdown
                }
                return prevCooldown - 1;
            });
        }, 1000);
        setTimerId(newTimerId);
    };

    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        setIsError(false);
        try {
            const response = await requestPatientOtp(mobile);
            setOtpSent(true);
            setMessage(response.message || 'OTP sent successfully!');
            startResendTimer(); // Start the timer on successful OTP request
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error requesting OTP.';
            setMessage(errorMessage);
            setIsError(true);
        } finally {
            setLoading(false);
        }
    };
    
    // --- NEW FUNCTION TO HANDLE RESENDING OTP ---
    const handleResendOtp = async () => {
        if (resendCooldown > 0) return; // Prevent clicking during cooldown

        setLoading(true);
        setMessage('Resending OTP...');
        setIsError(false);
        try {
            const response = await requestPatientOtp(mobile); // Reuse the same OTP request function
            setMessage(response.message || 'New OTP sent successfully!');
            setOtp(''); // Clear old OTP from input
            startResendTimer(); // Restart the cooldown timer
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error resending OTP.';
            setMessage(errorMessage);
            setIsError(true);
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        setIsError(false);
        try {
            const result = await login(mobile, otp);
            if (result.success) {
                setMessage(result.message);
                setTimeout(() => {
                    window.location.href = '/history';
                }, 500);
            } else {
                setMessage(result.message);
                setIsError(true);
            }
        } catch (error) {
            setMessage('Invalid OTP or login failed. Please try again.');
            setIsError(true);
        } finally {
            setLoading(false);
        }
    };
    
    const messageClassName = isError ? 'auth-message error' : 'auth-message success';

    return (
        <>
            <Navbar/>
            <div className="auth-page">
                <div className="auth-container">
                    <div className="auth-visual">
                        <h2>Welcome Back</h2>
                        <p>Securely access your patient dashboard.</p>
                    </div>
                    <div className="auth-form-wrapper">
                        <h3>Patient Login</h3>
                        {message && <div className={messageClassName}>{message}</div>}
                        
                        {!otpSent ? (
                            <form onSubmit={handleRequestOtp} className="auth-form">
                                <p className="form-instruction">Enter your registered mobile number to receive an OTP.</p>
                                <div className="form-groups">
                                    <PhoneIcon />
                                    <input
                                        type="tel"
                                        placeholder="10-digit Mobile Number"
                                        value={mobile}
                                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                                        required
                                        pattern="\d{10}"
                                        maxLength="10"
                                        title="Please enter a valid 10-digit mobile number"
                                        disabled={loading}
                                    />
                                </div>
                                <button type="submit" className="auth-button primary" disabled={loading}>
                                    {loading ? <LoadingSpinner /> : 'Request OTP'}
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={handleLogin} className="auth-form">
                                <p className="form-instruction">An OTP has been sent to <strong>{mobile}</strong>.</p>
                                <div className="form-groups">
                                    <KeyIcon />
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        placeholder="Enter 6-digit OTP"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                        required
                                        pattern="\d{6}"
                                        maxLength="6"
                                        title="Please enter the 6-digit OTP"
                                        disabled={loading}
                                    />
                                </div>
                                <button type="submit" className="auth-button primary" disabled={loading}>
                                    {loading ? <LoadingSpinner /> : 'Verify & Login'}
                                </button>
                                
                             

                                <button 
                                    type="button" 
                                    onClick={() => { setOtpSent(false); setMessage(''); setOtp(''); if (timerId) clearInterval(timerId); setResendCooldown(0); }} 
                                    className="auth-button secondary"
                                    disabled={loading}
                                >
                                    Change Mobile Number
                                </button>
                                   {/* --- RESEND OTP UI --- */}
                                <div className="resend-container">
                                    {resendCooldown > 0 ? (
                                        <p className="cooldown-text">
                                            Resend OTP in {resendCooldown}s
                                        </p>
                                    ) : (
                                        <button  
                                        style={{textAlign:"center",justifyContent:"center",alignItems:"center",display:"flex",marginTop:"10px"}}
                                            type="button"
                                            onClick={handleResendOtp}
                                            className="auth-button primary"
                                            disabled={loading}
                                        >
                                            Resend OTP
                                        </button>
                                    )}
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
            <Footer/>
        </>
    );
};

export default AuthForm;